import FullPageLoader from "@src/common/FullPageLoader";
import { DOC_COLOR_CODE, DOCUMENT_CATEGORY, OCR_EXIST_PARENT_ID } from "@src/config/constant";
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import OcrImg  from "../../styles/webroot/images/ocr-img.png";
import VehicleImg  from "../../styles/webroot/images/livecar-img.png";
import { isMobile } from "react-device-detect";

const isDeletedDocId = (deletedItems: any[], docId: any) =>
    !!deletedItems?.some((d: any) => Number(d?.id ?? d) === Number(docId))

export const DocumentView = (props: any): any => {

    const [loading, setLoading] = useState(false);
    const [activeDocument, setActiveDocument] = useState<any>(null)
    const [documents, setDocuments] = useState<any>([])
    const prevActiveDocumentRef = useRef<any>(null)

    const queryParams = useParams()
    const [searchParams] = useSearchParams()
    const isEditMode = props?.isEditMode || searchParams.get('edit') === 'true'
    // In edit mode path has :id but uploads should remain enabled
    const resubmissionLeadId = isEditMode ? undefined : queryParams.id

    const updateDocumentList = (params: any) => {
        props?.updateDocList(params)
    }

    const handleSelectionChange = () => {
        if (!activeDocument) return

        const inactiveDocId = activeDocument === DOCUMENT_CATEGORY['orcr']
            ? DOCUMENT_CATEGORY['existing_policy']
            : DOCUMENT_CATEGORY['orcr']
        // Only clear/mark-delete on an actual user radio switch (not initial preselect)
        const isUserSwitch = prevActiveDocumentRef.current != null
            && Number(prevActiveDocumentRef.current) !== Number(activeDocument)
        prevActiveDocumentRef.current = activeDocument

        let newDeletedItems = JSON.parse(JSON.stringify(props?.filesData?.deletedItems || []))
        const deletedIds = new Set(newDeletedItems.map((d: any) => Number(d?.id ?? d)))

        const updatedDocumentList = documents?.map((e: any) => {
            let updatedItem = { ...e }
            const isOrcrPolicyGroup = updatedItem?.type === 'single_selection'
                || [OCR_EXIST_PARENT_ID, DOCUMENT_CATEGORY['orcr'], DOCUMENT_CATEGORY['existing_policy']].includes(Number(updatedItem.value))

            if (isOrcrPolicyGroup && updatedItem?.child?.length) {
                updatedItem.child = updatedItem.child.map((child: any) => {
                    if (Number(child.value) !== Number(inactiveDocId)) return { ...child }

                    // On radio switch: remove opposite section from UI and queue deletes for addDocumentWeb
                    if (isUserSwitch) {
                        if (isEditMode && child.src?.length) {
                            for (const img of child.src) {
                                if (!img?.id || deletedIds.has(Number(img.id))) continue
                                newDeletedItems.push({
                                    id: Number(img.id),
                                    document_id: Number(img.document_id || inactiveDocId),
                                    document_type_id: Number(img.document_type_id || 1),
                                    type: 'delete'
                                })
                                deletedIds.add(Number(img.id))
                            }
                        }
                        return {
                            ...child,
                            src: [],
                            is_edit_doc: false
                        }
                    }
                    return { ...child }
                })

                const exItem = updatedItem.child.find((c: any) => Number(c.value) === Number(activeDocument))
                if (exItem) {
                    updatedItem = {
                        ...updatedItem,
                        value: exItem.value,
                        label: exItem.label,
                        src: exItem?.src || [],
                        is_edit_doc: exItem?.is_edit_doc || false,
                        is_resubmit: exItem?.is_resubmit,
                        comment: exItem?.comment
                    }
                }
            }
            return updatedItem
        })

        setDocuments(updatedDocumentList)
        updateDocumentList(updatedDocumentList)

        if (isUserSwitch) {
            const updatedFiles = { ...props.filesData }
            delete updatedFiles[inactiveDocId]
            if (isEditMode) {
                updatedFiles.deletedItems = newDeletedItems
            }
            props.updateSelectedFiles(updatedFiles)
        }
    }

    const markDocumentDeletedLocally = (deletedDoc: any) => {
        const entry = typeof deletedDoc === 'object' && deletedDoc !== null
            ? {
                id: Number(deletedDoc.id),
                document_id: Number(deletedDoc.document_id),
                document_type_id: Number(deletedDoc.document_type_id || 1),
                type: 'delete'
            }
            : { id: Number(deletedDoc), document_id: 0, document_type_id: 1, type: 'delete' }

        props.updateSelectedFiles({
            ...props.filesData,
            deletedItems: [
                ...JSON.parse(JSON.stringify(props?.filesData?.deletedItems || [])),
                entry
            ]
        })
    }

    const handleResubDocDelete = (img: any, documentId: any) => {
        // Edit / Resubmission: remove from UI immediately; persist via addDocumentWeb on submit
        if (!img?.id) return
        const resolvedDocumentId = Number(img.document_id || documentId)
        if (!resolvedDocumentId) return

        markDocumentDeletedLocally({
            id: Number(img.id),
            document_id: resolvedDocumentId,
            document_type_id: Number(img.document_type_id || 1),
            type: 'delete'
        })

        // Strip image only from its own section
        if (isEditMode) {
            const stripFromList = (list: any[]): any[] => (list || []).map((e: any) => {
                let updated = { ...e }
                if (Number(updated.value) === resolvedDocumentId && updated.src?.length) {
                    updated.src = updated.src.filter((srcImg: any) => Number(srcImg.id) !== Number(img.id))
                }
                if (updated.child?.length) {
                    updated.child = stripFromList(updated.child)
                    if (updated.type === 'single_selection' || Number(updated.value) === OCR_EXIST_PARENT_ID
                        || [DOCUMENT_CATEGORY['orcr'], DOCUMENT_CATEGORY['existing_policy']].includes(Number(updated.value))) {
                        const activeChild = updated.child.find((c: any) => Number(c.value) === Number(activeDocument || updated.value))
                            || updated.child.find((c: any) => Number(c.value) === resolvedDocumentId)
                        if (activeChild) {
                            updated.src = activeChild.src || []
                            updated.is_edit_doc = activeChild.is_edit_doc
                        }
                    }
                }
                return updated
            })
            const nextDocs = stripFromList(documents)
            setDocuments(nextDocs)
            updateDocumentList(nextDocs)
        }
    }
    
    useEffect(() => {
        if (props?.docList?.length) {
            setDocuments(props.docList)
        }
        //eslint-disable-next-line
    }, [props?.docList])

    useEffect(() => {
        handleSelectionChange()
        //eslint-disable-next-line
    }, [activeDocument])

    let response = documents && documents?.length > 0 && documents?.map((item: any, index: number) => {
        //orcr existing policy case
        if (item?.type === 'single_selection' && !activeDocument) {
            //if resubmission/edit case, then find item with src length to preselect
            const itemWithResubmitDoc = item?.child?.find((e: any) => e?.src?.length || e.is_resubmit || e.is_edit_doc)

            if (item.value !== OCR_EXIST_PARENT_ID) {
                const orcrItem = item?.child?.find((e: any) => e.value === item.value)
                item = { ...item, value: orcrItem.value, label: orcrItem.label }
                setActiveDocument(item.value)
            } else {
                const preferredChild = item?.child?.find((e: any) => e.value === DOCUMENT_CATEGORY['orcr'] && (e?.src?.length || e.is_resubmit || e.is_edit_doc))
                    || itemWithResubmitDoc
                    || item?.child?.find((e: any) => e.value === DOCUMENT_CATEGORY['orcr'])
                const orcrItem = item?.child?.find((e: any) => e.value === (preferredChild?.value || DOCUMENT_CATEGORY['orcr']))

                item = { ...item, value: orcrItem.value, label: orcrItem.label, src: orcrItem.src || [] }
                setActiveDocument(item.value)
            }
        }

        //fill orcr/existing policy with the active section's docs only (children keep their own src independently)
        if (item?.type === 'single_selection') {
            let childData = item?.child?.find((e: any) => e?.value === item?.value)
            let updatedItem = { ...item }
            updatedItem['src'] = childData?.src || []
            updatedItem['comment'] = childData?.comment
            updatedItem['is_resubmit'] = childData?.is_resubmit
            updatedItem['is_edit_doc'] = childData?.is_edit_doc

            item = { ...updatedItem }
        }

        if (item?.child?.length && item?.type !== 'single_selection') {
            // return <DocumentView docList={item.child} handleFileChange={props.handleFileChange} parent={item.label} errField={props.errField} key={index} updateSelectedFiles={props.updateSelectedFiles} filesData={props.filesData} for={props?.for} updateDocList={props.updateDocList} addComment={props.addComment} />
        }
        let parentName = (props.parent) ? props.parent : ''

        const removeSelectedFile = (item: any, fileIdx: any) => {
            setLoading(true);
            let selectedFiles = props.filesData;
            //CREATE NORMAL ARRAY & REMOVE DELETED FILE
            const newFileList: any = Array.from(selectedFiles[item.value]).filter((file: any, key: number) => { return key !== +fileIdx; });

            //CREATE FILE ARRAY
            let updatedDocList: any = new DataTransfer();
            for (let doc of newFileList) {
                let newFile: any = new File([doc], doc.name, { type: doc.type })
                updatedDocList.items.add(newFile);

            }
            selectedFiles[item.value] = updatedDocList.files.length ? updatedDocList.files : undefined;
            if (!selectedFiles[item.value]) delete selectedFiles[item.value]

            //UPDATE PARENT STATE
            props.updateSelectedFiles(selectedFiles);

            //UPDATE INPUT FILE
            const fileBox = document.getElementById(`upload-image-input-${item.value}`) as any;
            fileBox.files = updatedDocList.files;

            //for ORCR image recognition API call
            if (item.value === DOCUMENT_CATEGORY['orcr'] && props.for === 'agency') {
                props.setOrcrApiCall(false)
            }


            setTimeout(() => {
                setLoading(false);
            }, 500)

        }

        const renderVehiclePhotos = (item: any) => {

            
            if (item.value !== DOCUMENT_CATEGORY['vehicle_photos']) return null;

            const removeVehiclePhotos = (child: any, uploadIndex: number) => {
                const docId = child.src?.[uploadIndex]?.id

                const clearVehiclePhotoSrc = () => {
                    const updatedChild = item.child.map((el: any) => {
                        if (el.value === child.value) {
                            const updatedSrc = [...(el.src || [])];
                            updatedSrc[uploadIndex] = { ...updatedSrc[uploadIndex], src: undefined };
                            return {
                                ...el,
                                src: updatedSrc,
                            };
                        }
                        return el;
                    })

                    const updatedDocumentList = documents?.map((e: any) => {
                        let updatedItem = { ...e }
                        if (e.value === item.value) {
                            updatedItem = { ...updatedItem, child: updatedChild }
                        }
                        return updatedItem
                    })
                    props.updateDocList(updatedDocumentList)
                }

                clearVehiclePhotoSrc()
                if (docId) {
                    markDocumentDeletedLocally({
                        id: Number(docId),
                        document_id: Number(child.value),
                        document_type_id: 1,
                        type: 'delete'
                    })
                }
            }
            
            return (
                <div className="col-md-12">
                {item.child?.map((subChild: any, subIndex: number) => (
                    <div key={`${item.label}-${subIndex}`} className="row">
                    <span className="col-md-12 upload-images-title">{subChild.label}</span>

                    {Array.from(
                        { length: subChild.max_upload || 1 },
                        (_, uploadIndex) => {
                            
                        let file =
                            props.filesData?.[subChild.value]?.[uploadIndex];

                            
                            if(!file && (subChild.is_resubmit || subChild.is_edit_doc)){
                                file = subChild?.src?.[uploadIndex]?.src
                            }  
                            // Hide soft-deleted existing docs in edit/resubmission
                            if (file && subChild?.src?.[uploadIndex]?.id && isDeletedDocId(props?.filesData?.deletedItems, subChild.src[uploadIndex].id)) {
                                file = undefined
                            }
                            let isFileString = typeof file === 'string'? true : false
                            
                         

                        let ext = file?.name?.split('.').pop()?.toLowerCase();
                        if (['csv', 'xls', 'xlsx'].includes(ext)) ext = 'excel';
                        if (['doc', 'docx'].includes(ext)) ext = 'word';

                        return (
                            <div
                            key={`${subIndex}-${uploadIndex}`}
                            className="col-md-2 col-xs-6 p-lg-t"
                            >
                                {
                                    !file && (
                                        <div className="add-document-proof" role="presentation">
                                           <input
                                                type="file"
                                                name="documents[]"
                                                onChange={(e: any) =>
                                                props.handleFileChange(
                                                    e,
                                                    subChild,
                                                    uploadIndex
                                                )
                                                }
                                            />

                                            <i className="ic-add"></i>
                                            <span>Upload</span>

                                        
                                        </div>
                                    )
                                }

                                {/* 🔥 Preview INSIDE upload box */}
                                {file && (
                                    <div className="upload-image-proof mt-2" id={`upload-image-input-${subChild.value}`}>
                                        <span
                                        className="close-icn"
                                        onClick={() =>
                                           isFileString ? removeVehiclePhotos(subChild,uploadIndex) : removeSelectedFile(subChild, uploadIndex)
                                        }
                                        >
                                        <i className="ic-clearclose"></i>
                                        </span>

                                        {(['jpg', 'jpeg', 'png'].includes(ext) || isFileString) ? (
                                        <img
                                            src={isFileString? file: URL.createObjectURL(file)}
                                            alt=""
                                        />
                                        ) : (
                                        <a
                                            href={isFileString? file: URL.createObjectURL(file)}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <i className={`ic-${ext}-file f-50`} />
                                        </a>
                                        )}
                                </div>
                                )}

                            </div>
                        );
                        }
                    )}

                     {
                                    subChild.is_resubmit
                                    &&
                                    <div className="col-md-3 p-lg-t resubmission-text-div">
                                        <span className="reuploadText">
                                            <i className='ic-warning m-sm-r'></i>
                                            {"Reupload: " + subChild.comment}
                                        </span>
                                    </div>
                               }

                                {
                                    subChild.is_resubmit
                                    &&
                                    <div className="row document-remarks textarea-field">
                                        <fieldset className="form-filed">
                                            <div className="material">
                                                <textarea className='form-input' placeholder=' ' onChange={(e: any) => { props.addComment(subChild, e.target.value) }}></textarea>
                                                <label data-label="Remarks" className="form-label"></label>
                                            </div>
                                        </fieldset>
                                    </div>
                                }
                    </div>
                ))}
                </div>
            );
        };

const isOrcrOrPolicy = item.label === 'ORCR' || item.label === 'Existing Policy';
const isVehiclePhotos = item.label === 'Vehicle Photos';


        return (
            <div className={""} key={index}>
                <FullPageLoader show={loading} />
                <div className={`row`}> 
                    <>
                        <div className='col-md-12 w-100'>
                            <div className='upload-heading-text ' style={{
                                background: isMobile
                                    ? item.value === DOCUMENT_CATEGORY['vehicle_photos']
                                    ? DOC_COLOR_CODE['VEHICLE']
                                    : item.value === DOCUMENT_CATEGORY['orcr']
                                    ? DOC_COLOR_CODE['OCR']
                                    : item.value === DOCUMENT_CATEGORY['others']
                                    ? DOC_COLOR_CODE['OTHER']
                                    : ""
                                    : ""
                                }}>
                                {
                                    <>
                                    {
                                        isMobile && <span className="doc-upload-icn"><img src={ item.value === DOCUMENT_CATEGORY['vehicle_photos'] ? VehicleImg : OcrImg} className="" alt="" /></span> 

                                    }
                                    <div>
                                        <h3> {isOrcrOrPolicy
                                            ? 'ORCR / Existing Policy'
                                            : isVehiclePhotos
                                                ? 'Vehicle Photo' : item.label + ` ${item.label.includes('Other') ? "" : 'Documents'}`}</h3>
                                        {isOrcrOrPolicy ? <span>Upload any one</span> : <span>Optional</span>}
                                    </div>
                                       
                                    </>
                                }
                            </div>
                        </div>
                        <div className='col-md-12 w-100'>
                            {
                                item?.type === 'single_selection'
                                &&
                                <>
                                    <div className='radio-btn m-xl-l m-lg-t doc-type-select-opt d-flex'>
                                        <div className="custom-control custom-checkbox col-md-2  col-xs-6">
                                            <input checked={activeDocument === DOCUMENT_CATEGORY['orcr']} id="orcr" type="radio" className="custom-control-input " name="ORCR" onChange={() => {
                                                setActiveDocument(DOCUMENT_CATEGORY['orcr'])
                                            }} disabled={resubmissionLeadId ? true : false} />
                                            <label className="custom-control-label">ORCR </label>
                                        </div>

                                        <div className="custom-control custom-checkbox col-md-2 col-xs-6">
                                            <input checked={activeDocument === DOCUMENT_CATEGORY['existing_policy']} id="existing_policy" type="radio" className="custom-control-input " name="Existing Policy" onChange={() => {
                                                setActiveDocument(DOCUMENT_CATEGORY['existing_policy'])
                                            }} disabled={resubmissionLeadId ? true : false} />
                                            <label className="custom-control-label">Existing Policy </label>
                                        </div>
                                    </div>
                                </>
                            }
                        </div> 
                    </> 
                    {
                        item.value === DOCUMENT_CATEGORY['vehicle_photos'] && (
                           renderVehiclePhotos(item)
                          )
                        }
                    {
                        item?.src?.length
                            ?
                            item.src.map((img: any, idx: number) => {
                                //hide deleted items
                                if (isDeletedDocId(props?.filesData?.deletedItems, img.id)) return false

                                let splitName = img?.src?.split('?')
                                let splitPathName = splitName?.[0]?.split('.')
                                let fileType = (splitPathName[splitPathName.length - 1])?.toLowerCase()
                                fileType = ['csv', 'xls', 'xlsx'].includes(fileType) ? 'excel' : (fileType === 'doc' || fileType === 'docx') ? fileType = 'word' : fileType

                                return (
                                    <div className="col-md-2 col-xs-6 p-lg-t" key={idx}>
                                        <div role="presentation" className="upload-image-proof">
                                            <span className='close-icn' onClick={() => { handleResubDocDelete(img, item.value) }}>
                                                <i className='ic-clearclose'></i></span>
                                            {
                                                fileType === 'jpg' || fileType === 'jpeg' || fileType === 'png' ?
                                                    <img src={img?.src} alt={"document_image_" + idx} />
                                                    : <a href={img?.src} target="_blank" rel="noreferrer"><i className={`ic-${fileType}-file f-50`}></i></a>
                                            }
                                        </div>
                                        <p className='doc-tag-name'>{parentName + ' ' + item.label}</p>

                                    </div>
                                )
                            })
                            : undefined
                    }
                    {
                        props.filesData && (props.filesData[item.value])
                            ?
                            Object.keys(props.filesData[item.value]).map((file: any, key: number) => {
                                let newfile = props.filesData[item.value][file]
                                let fileType = newfile?.name?.split('.')
                                fileType = fileType && fileType[fileType?.length - 1]
                                fileType = fileType && fileType.toLowerCase()
                                fileType = ['csv', 'xls', 'xlsx'].includes(fileType) ? 'excel' : (fileType === 'doc' || fileType === 'docx') ? fileType = 'word' : fileType

                                return (
                                    <div className="col-md-2 col-xs-6 p-lg-t" key={key}>
                                        <div role="presentation" className="upload-image-proof">
                                            <span className='close-icn' onClick={() => removeSelectedFile(item, file)}>
                                                <i className='ic-clearclose'></i></span>
                                            {fileType === 'jpg' || fileType === 'jpeg' || fileType === 'png' ? <img src={URL.createObjectURL(props.filesData[item.value][file])} alt="" /> : <a href={URL.createObjectURL(props.filesData[item.value][file])} target="_blank" rel="noreferrer"><i className={`ic-${fileType}-file f-50`}></i></a>}

                                        </div>
                                        {/* <p className='doc-tag-name'>{parentName + ' ' + item.label}</p> */}

                                    </div>
                                )
                            }
                            )
                            :
                            ""
                    }
                    {
                        item.is_resubmit
                        &&
                        <div className="col-md-3 p-lg-t resubmission-text-div">
                            <span className="reuploadText">
                                <i className='ic-warning m-sm-r'></i>
                                {"Reupload: " + item.comment}
                            </span>
                        </div>
                    }
                    {
                         item.value !== DOCUMENT_CATEGORY['vehicle_photos'] && (
                            <div className="col-md-2 col-xs-6 p-lg-t"> 
                                <div role="presentation" className="add-document-proof">
                                    {
                                        (resubmissionLeadId && !item?.is_resubmit) ?

                                            <input type="file" id={`upload-image-input-${item.value}`} name="documents[]" disabled />
                                            : <input type="file" id={`upload-image-input-${item.value}`} name="documents[]" onChange={(e: any) => props.handleFileChange(e, item)} multiple title={""} />
                                    }
                                    <i className="ic-add"> </i>
                                    <span>Upload</span>
                                </div>
                                {/* <p className='doc-tag-name'>{parentName + ' ' + item.label + (item.is_required ? '*' : (item.conditional_mandatory && item.conditional_mandatory.length) ? "**" : '')}</p> */}
                                {
                                    (props?.errField?.[item.value]) ?
                                        <span className="error-txt" style={{ position: 'relative' }}>{props?.errField?.[item.value]}</span> : null
                                }
                            </div>
                         )
                    }

                </div>
                {
                    item.is_resubmit
                    &&
                    <div className="row document-remarks textarea-field">
                        <fieldset className="form-filed">
                            <div className="material">
                                <textarea className='form-input' placeholder=' ' onChange={(e: any) => { props.addComment(item, e.target.value) }}></textarea>
                                <label data-label="Remarks" className="form-label"></label>
                            </div>
                        </fieldset>
                    </div>
                }

            </div >)
    })
    return response;
}